import React, { forwardRef, useEffect, useMemo, useState } from 'react';

import { Box, Center, Checkbox, HStack, Stack, Wrap } from '@chakra-ui/react';
import { cloneDeep, isArray, keys, uniq, isString, map } from 'lodash';
import filter from 'lodash/filter';
import { AiOutlineClose } from 'react-icons/all';
import { FiChevronDown, FiChevronRight } from 'react-icons/fi';
import Select, { components } from 'react-select';
import AsyncSelect from 'react-select/async';
import CreatableSelect from 'react-select/creatable';
import { colorOpacity, customTheme } from 'theme';

import { Tag } from 'components/DataDisplay';
import { TagStyles } from 'components/DataDisplay/Tag/types';
import { DropdownIndicator } from 'components/DataEntry/Select/utils';
import { FilterIcon } from 'components/Icons';
import { getIcon } from 'components/Icons/Components';

import { defaultCheckBoxStyles, defaultSelectStyles } from './styles';
import { SelectProps } from './types';

export const CustomSelect = forwardRef((props: SelectProps, ref) => {
  const {
    styles = {},
    overwriteDefaultStyles,
    checkBoxStyles,
    isMulti,
    value,
    showTotalSelected,
    selectedCount,
    height,
    options,
    inputValue,
    onChange: propOnChange,
    onClickGroupHeading,
    showGroupCollapse = false,
    allOption,
    creatable = false,
    showSelectedAtBottom = false,
    showSelectedAtBottomOutside = false,
    selectedOptionLabel,
    selectedOptionsStyles,
    selectedOptionTagStyles,
    onCreateOption,
    placeholder = 'Select...',
    alwaysShowPlaceholder = false,
    showIconInValueContainer = true,
    isAFilter = false,
    showClearIndicator = true,
    valueContainerIcon,
    selectedMessage,
    leftMessage,
    async = false,
    loadOptions,
    isDisabled,
    showUndefinedValueLabel = true,
    showDropdownIndicator = true,
    showIsLabel = true,
    optionIconHeight = 5,
    optionIconWidth = 5,
    showBorder = true,
    components: customComponents,
    ...rest
  } = props;

  const [selected, setSelected] = useState<Record<string, any>[]>([]);
  const [collapsedGroups, setCollapsedGroups] = useState<
    Record<string, boolean>
  >({});

  const getGroupKey = data => String(data?.value ?? data?.label ?? '');
  const toggleGroup = data => {
    const groupKey = getGroupKey(data);
    setCollapsedGroups(previous => ({
      ...previous,
      [groupKey]: !previous[groupKey],
    }));
  };

  const visibleOptions = useMemo(() => {
    if (!showGroupCollapse || !isArray(options)) return options;

    const optionByValue = new Map(
      options
        .filter(option => !Array.isArray((option as any)?.options))
        .map(option => [String((option as any)?.value), option]),
    );

    return options.filter(option => {
      if (Array.isArray((option as any)?.options)) return true;

      let parentValue = (option as any)?.parentValue;
      while (parentValue) {
        if (collapsedGroups[String(parentValue)]) return false;
        parentValue = (optionByValue.get(String(parentValue)) as any)
          ?.parentValue;
      }

      return true;
    });
  }, [collapsedGroups, options, showGroupCollapse]);

  useEffect(() => {
    isMulti && setSelected((value as Record<string, any>[]) ?? []);
  }, [value, isMulti]);

  const baseSelectStyles = useMemo(() => {
    const props = cloneDeep(
      defaultSelectStyles(
        height,
        showTotalSelected,
        isAFilter,
        !!leftMessage,
        showBorder,
      ),
    );

    const mergeStyles = () => {
      const styleNames = uniq([...keys(props), ...keys(styles)]);

      return styleNames.reduce(
        (o, name) => ({
          ...o,
          [name]: (...args) => {
            const defaultStyle = props[name] ? props[name](...args) : {};
            const customStyle = styles[name] ? styles[name](...args) : {};

            return {
              ...defaultStyle,
              ...customStyle,
            };
          },
        }),
        {} as any,
      );
    };

    return overwriteDefaultStyles ? styles : mergeStyles();
    // leftMessage/showBorder also feed the default styles, so recompute on them.
  }, [
    height,
    overwriteDefaultStyles,
    styles,
    showTotalSelected,
    isAFilter,
    leftMessage,
    showBorder,
  ]);

  const renderLabel = props => {
    const { data } = props;
    return (
      <HStack w="full" color="selectIcon">
        {data?.icon && (
          <Center>
            <Center w={optionIconWidth} h={optionIconHeight}>
              {isString(data?.icon) ? getIcon(data?.icon) : data?.icon}
            </Center>
          </Center>
        )}
        <Box flex={1}>
          {data?.label}
          {/* an optional second line under the label, such as an entity's account */}
          {data?.subLabel && (
            <Box fontSize="xs" color={customTheme.colors.gray['250']}>
              {data.subLabel}
            </Box>
          )}
        </Box>
      </HStack>
    );
  };

  const renderLeftMessage = value => (
    <Box
      color={customTheme.colors.gray['300']}
      whiteSpace="nowrap"
      pr={showIsLabel ? 0 : 1}
      borderBottom={!!value || showUndefinedValueLabel ? '1px dotted' : 'none'}
      cursor={isDisabled ? 'not-allowed' : 'pointer'}
    >
      {leftMessage}
    </Box>
  );
  const renderValue = value => (
    <Box
      color={customTheme.colors.blue['300']}
      bg={colorOpacity(customTheme.colors.blue['300'], 0.1)}
      borderRadius={4}
      px={2}
      w="full"
    >
      <Box textOverflow="ellipsis" whiteSpace="nowrap">
        {value}
      </Box>
    </Box>
  );

  const SingleValue = props => {
    const { data, hasValue, getValue } = props;
    const v = getValue()?.[0]?.value;

    /*return null;
    if (leftMessage && isAFilter) return null;*/
    return (
      <components.SingleValue {...props}>
        <HStack w="full" spacing={0}>
          {showIconInValueContainer &&
            (isAFilter || !!valueContainerIcon) &&
            !leftMessage && (
              <Center h="full" boxSize={7} pr={2}>
                <Center w="full">
                  {valueContainerIcon ?? data?.icon ?? (
                    <FilterIcon color="primary" />
                  )}
                </Center>
              </Center>
            )}
          {leftMessage && renderLeftMessage(v)}
          {leftMessage &&
            isAFilter &&
            (!!v || showUndefinedValueLabel) &&
            showIsLabel && <Box px={1}>Is</Box>}
          {leftMessage && isAFilter ? (
            !!v || showUndefinedValueLabel ? (
              renderValue(props.children)
            ) : (
              <></>
            )
          ) : (
            <Box
              flex={1}
              overflow="hidden"
              textOverflow="ellipsis"
              //color={hasValue && isAFilter && !!v ? 'white' : 'inherit'}
            >
              {props.children}
            </Box>
          )}
        </HStack>
      </components.SingleValue>
    );
  };

  const renderFilterIcon = (size = 6, color = 'primary') => (
    <Center h="full" boxSize={size}>
      <Center w="full" color={color}>
        <FilterIcon />
      </Center>
    </Center>
  );

  const Placeholder = props => {
    //if (leftMessage && isAFilter) return renderLeftMessage();
    return (
      <components.Placeholder {...props}>
        <HStack w="full" h="full">
          {(showIconInValueContainer || isAFilter) && !leftMessage && (
            <Center boxSize={5}>
              <Center color="primary" w="full">
                {valueContainerIcon ?? <FilterIcon />}
              </Center>
            </Center>
          )}
          {leftMessage ? (
            renderLeftMessage(undefined)
          ) : (
            <Box w="full" overflow="hidden" textOverflow="ellipsis">
              {props.children}
            </Box>
          )}
        </HStack>
      </components.Placeholder>
    );
  };

  // render options
  const Option = props => {
    const { isSelected, data, isDisabled } = props;
    const hasChildren = data?.descendantValues?.length > 0;
    const collapseButton = showGroupCollapse && hasChildren && (
      <Box
        as="button"
        type="button"
        aria-label={`${
          collapsedGroups[getGroupKey(data)] ? 'Expand' : 'Collapse'
        } ${data.label}`}
        display="flex"
        alignItems="center"
        justifyContent="center"
        boxSize={5}
        flexShrink={0}
        onClick={event => {
          event.stopPropagation();
          toggleGroup(data);
        }}
      >
        {collapsedGroups[getGroupKey(data)] ? (
          <FiChevronRight />
        ) : (
          <FiChevronDown />
        )}
      </Box>
    );

    return (
      <components.Option {...props}>
        {isMulti ? (
          <HStack w="full">
            {/* @ts-ignore */}
            <Checkbox
              flex={1}
              isChecked={isSelected}
              isIndeterminate={data?.isIndeterminate}
              isDisabled={isDisabled}
              value={data.label}
              {...defaultCheckBoxStyles}
              {...checkBoxStyles}
            >
              {renderLabel(props)}
            </Checkbox>
            {collapseButton}
          </HStack>
        ) : (
          <HStack w="full" opacity={isDisabled ? 0.4 : 1}>
            <Box flex={1}>{renderLabel(props)}</Box>
            {collapseButton}
          </HStack>
        )}
      </components.Option>
    );
  };

  const ClearIndicator = props => {
    const { getValue } = props;
    const val = getValue()?.[0]?.value;
    return val && showClearIndicator && !alwaysShowPlaceholder ? (
      <components.ClearIndicator {...props}>
        <Box>
          <Center boxSize={3} cursor="pointer">
            <AiOutlineClose color={customTheme.colors.primary} />
          </Center>
        </Box>
      </components.ClearIndicator>
    ) : null;
  };

  const MultiValue = props => {
    const { children, index, getValue, hasValue, isDisabled } = props;
    const values = getValue();
    let length = selectedCount ?? values?.length;
    //if (leftMessage && isAFilter) return null;

    const maxToShow = 0;

    if (showTotalSelected || alwaysShowPlaceholder || leftMessage) {
      return index < maxToShow && !alwaysShowPlaceholder && !leftMessage ? (
        <components.MultiValue {...props} />
      ) : index === maxToShow ? (
        <Box
          {...(alwaysShowPlaceholder ? baseSelectStyles?.placeholder() : {})}
        >
          <HStack align="center">
            {showIconInValueContainer &&
              (valueContainerIcon ? (
                <Center h="full" boxSize={5} color="primary">
                  {valueContainerIcon}
                </Center>
              ) : (
                renderFilterIcon(
                  4,
                  'primary',
                  /*hasValue && isAFilter ? 'white' : 'primary',*/
                )
              ))}
            {alwaysShowPlaceholder ? (
              <Box>{placeholder}</Box>
            ) : (
              <>
                {leftMessage ? (
                  <HStack spacing={1}>
                    {renderLeftMessage(length)}
                    {!!length && !showTotalSelected && (
                      <Box whiteSpace="nowrap">Is</Box>
                    )}

                    <Tag
                      label={
                        showTotalSelected
                          ? length
                          : map(values, o => o?.label)?.join(' or ')
                      }
                      styles={{
                        tag: {
                          py: 1,
                          px: 2,
                          border: 'none',
                          borderRadius: 4,
                          bg: colorOpacity(customTheme.colors.blue['300'], 0.1),
                        },
                        label: {
                          color: customTheme.colors.blue['300'],
                          fontSize: showTotalSelected ? 'inherit' : '16px',
                        },
                      }}
                    />
                  </HStack>
                ) : (
                  <Center py={0} h="full" opacity={isDisabled ? 0.5 : 1}>
                    {length} {selectedMessage || 'selected'}
                  </Center>
                )}
              </>
            )}
          </HStack>
        </Box>
      ) : null;
    }

    return (
      <components.MultiValue {...props}>
        <HStack>
          {showIconInValueContainer && renderFilterIcon()}
          <Box>{children}</Box>
        </HStack>
      </components.MultiValue>
    );
  };

  const formatGroupLabel = data => (
    <HStack
      justify="space-between"
      onClick={() => onClickGroupHeading?.(data?.options)}
      cursor={onClickGroupHeading ? 'pointer' : 'inherit'}
      borderBottom="1px solid"
      borderColor="componentBorder"
      fontSize="14px"
      px={3}
    >
      {data.showCheckbox ? (
        <Checkbox
          pointerEvents="none"
          isChecked={data.isChecked}
          isIndeterminate={data.isIndeterminate}
          {...defaultCheckBoxStyles}
          {...checkBoxStyles}
        >
          {data.label}
        </Checkbox>
      ) : (
        <Box color="primary">{data.label}</Box>
      )}
      <HStack spacing={2}>
        {data.showOptionCount !== false && <Box>{data.options.length}</Box>}
        {showGroupCollapse && (
          <Box
            as="button"
            type="button"
            aria-label={`${
              collapsedGroups[getGroupKey(data)] ? 'Expand' : 'Collapse'
            } ${data.label}`}
            display="flex"
            alignItems="center"
            justifyContent="center"
            boxSize={5}
            onClick={event => {
              event.stopPropagation();
              toggleGroup(data);
            }}
          >
            {collapsedGroups[getGroupKey(data)] ? (
              <FiChevronRight />
            ) : (
              <FiChevronDown />
            )}
          </Box>
        )}
      </HStack>
    </HStack>
  );

  const Group = props => (
    <components.Group {...props}>
      {collapsedGroups[getGroupKey(props.data)] ? null : props.children}
    </components.Group>
  );

  const values = {
    ref: ref as any,
    openMenuOnFocus: true,
    isMulti,
    closeMenuOnSelect: !isMulti,
    hideSelectedOptions: isMulti && showSelectedAtBottom,
    isDisabled,
    components: {
      ClearIndicator,
      DropdownIndicator: showDropdownIndicator ? DropdownIndicator : null,
      Option,
      MultiValue,
      SingleValue,
      Placeholder,
      ...(showGroupCollapse ? { Group } : {}),
      //ValueContainer,
      ...customComponents,
    },
    placeholder: placeholder,
    formatGroupLabel: formatGroupLabel,
    styles: baseSelectStyles,
    isClearable: true,
    options: allOption
      ? [allOption, ...(visibleOptions || [])]
      : visibleOptions,

    onChange: (selected: any, event: any) => {
      if (isArray(selected)) {
        let result: any[] = [];
        if (selected.length > 0 && allOption) {
          if (selected[selected.length - 1].value === allOption.value) {
            result = [allOption, ...(options || [])];
            // Store the list itself; it used to be nested as [[...]].
            setSelected(result);
            return propOnChange?.(result, event);
          }

          if (selected.length === options?.length) {
            if (selected.some(({ label }) => label === '*')) {
              result = selected.filter(
                option => option?.value !== allOption?.value,
              );
            } else if (
              // The picked option is on the event, not on the action string.
              event.action === 'select-option' &&
              event?.option?.value !== '*'
            ) {
              result = [allOption, ...options];
            }
            setSelected(result);
            return propOnChange?.(result, event);
          }
        }
      }

      //if all option is present in list
      if (isMulti && selected.find(o => o.value === 'all'))
        return propOnChange?.(options?.slice(1) ?? [], event);

      if (!isMulti && selected === null) {
        let val: any = null;
        if (
          !!options?.length &&
          (options?.[0] as Record<string, any>)?.value === undefined
        )
          val = options[0];
        return propOnChange?.(val, event);
      }

      return propOnChange?.(selected, event);
    },
    //menuPortalTarget={document.body} // commented because valueContainer is not fixed with the parent container on scroll. This is a react-select bug reported over github.
    //menuPlacement: 'auto',
    //blurInputOnSelect={!isMulti}
    //autoBlur
    ...rest,
    ...(inputValue !== undefined ? { inputValue } : {}),
    value:
      isMulti && showSelectedAtBottom
        ? selected
        : value ?? (isMulti ? [] : null),
  };

  return (
    <Stack>
      {creatable ? (
        <CreatableSelect
          menuPlacement="auto"
          onCreateOption={onCreateOption}
          {...values}
        />
      ) : async ? (
        <AsyncSelect
          menuPlacement="auto"
          cacheOptions
          noOptionsMessage={() => 'Type to Search'}
          defaultOptions={options}
          loadOptions={loadOptions}
          {...values}
        />
      ) : (
        <Select menuPlacement="auto" {...values} />
      )}
      {isMulti &&
        showSelectedAtBottom &&
        !showSelectedAtBottomOutside &&
        selected.length > 0 && (
          <Box {...selectedOptionsStyles}>
            <SelectedOptions
              selected={selected}
              isDisabled={isDisabled}
              styles={selectedOptionTagStyles}
              selectedOptionLabel={selectedOptionLabel}
              onRemove={(option, event) => {
                const filtered = filter(
                  selected,
                  s => s.value !== option.value,
                );
                setSelected(filtered);
                propOnChange?.(filtered, event);
              }}
            />
          </Box>
        )}
    </Stack>
  );
});

interface SelectedOptionsProps {
  selected: Record<string, any>[];
  isDisabled?: boolean;
  styles?: TagStyles;
  selectedOptionLabel?: (option: Record<string, any>) => React.ReactNode;
  onRemove: (option: Record<string, any>, event?: any) => void;
}

export const SelectedOptions = ({
  selected,
  isDisabled,
  styles,
  selectedOptionLabel,
  onRemove,
}: SelectedOptionsProps) => (
  <Wrap>
    {map(selected, option => (
      <Tag
        label={
          <HStack>
            {option.icon && (
              <Box boxSize={4}>
                <Center>{option.icon}</Center>
              </Box>
            )}
            <Box>{selectedOptionLabel?.(option) ?? option.label}</Box>
          </HStack>
        }
        closeButton={!isDisabled}
        value={option}
        onClose={value => onRemove(value)}
        styles={{
          tag: {
            fontSize: 'sm',
            py: 0.5,
            px: 2,
            mr: 1,
            bg: 'hover',
            border: 'none',
            ...styles?.tag,
          },
          label: styles?.label,
          closeButton: {
            borderRadius: 'full',
            bg: 'white',
            ...styles?.closeButton,
          },
        }}
      />
    ))}
  </Wrap>
);
